import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from "react";
import { MuiMaterials } from "../@index.js";
import * as global from "../data/global";
import { createApiEndpoint } from "../index.js";
import InventorySummaryTable from "./InventorySummaryTable.js";
import { ModalChrome, ModalBody, SiteSelectPhase, SITE_WINDOW_IDS, containedActionButtonSx, CenteredLoadingOverlay, withScrollHintMenuProps } from "./windowChrome/index.js";

const getThemeColor = (lightColor, darkColor) =>
    global.gw.theme === "light" ? lightColor : darkColor;

// One US bushel == 1.2445 cuft -> 1 / 1.2445 ≈ 0.80356 bushels per cuft. Used as a fallback
// when the BTS Bin row doesn't carry a BushelsPerCubicFoot value (older legacy data).
const DEFAULT_BUSHELS_PER_CUBIC_FOOT = 0.803563913;
const FT_TO_M = 0.3048;
const FILL_MODES = [
    { id: "percent", label: "Percent Full (%)" },
    { id: "height", label: "Grain Height (ft)" },
    { id: "headroom", label: "Headroom (ft)" },
    { id: "bushels", label: "Bushels" },
];

// USDA / standard test-weight fallbacks (lb/bushel). Used when neither the BTS Grain reference
// table nor the legacy Bin row carry a non-zero BushelWeight, so Product Weight always renders
// instead of silently collapsing to 0.
const DEFAULT_BUSHEL_WEIGHTS_LB = {
    corn: 56,
    "shelled corn": 56,
    wheat: 60,
    "hard red wheat": 60,
    "soft red wheat": 60,
    "white wheat": 60,
    soybeans: 60,
    soybean: 60,
    soya: 60,
    oats: 32,
    barley: 48,
    rye: 56,
    sorghum: 56,
    milo: 56,
    sunflower: 28,
    "oil sunflower": 28,
    "non-oil sunflower": 24,
    canola: 50,
    flax: 56,
    rice: 45,
    "rough rice": 45,
    buckwheat: 48,
    millet: 50,
    triticale: 56,
};
const DEFAULT_FALLBACK_BUSHEL_WEIGHT_LB = 56; // generic dense-grain default

const parseDecompressed = async (res, key) => {
    if (!res || typeof res !== "object") return null;
    const raw = res?.data?.[key] ?? res?.[key];
    if (!raw || typeof raw !== "string") return null;
    try {
        const ab = global.base64StringToArrayBuffer(raw);
        const decompressed = await global.decompress(ab, "gzip");
        return decompressed ? JSON.parse(decompressed) : null;
    } catch {
        return null;
    }
};

// Pulls out a numeric BinId regardless of whether the row uses PascalCase (legacy SP DataTable
// JSON) or camelCase (EF model JSON). Same convention used by global.findBinLevelRowForCardBin.
const readBinId = (row) => {
    if (!row) return null;
    const raw = row.BinId ?? row.binId ?? row.binDataId ?? row.number;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
};

const readNumber = (row, ...keys) => {
    if (!row) return null;
    for (const k of keys) {
        const v = row[k];
        if (v == null || v === "") continue;
        const n = typeof v === "number" ? v : parseFloat(v);
        if (!Number.isNaN(n)) return n;
    }
    return null;
};

const readString = (row, ...keys) => {
    if (!row) return "";
    for (const k of keys) {
        const v = row[k];
        if (v != null && String(v).trim() !== "") return String(v);
    }
    return "";
};

// Server stores MeasurementDate as naive datetime2 — no TZ. Send local wall clock here (not UTC via toISOString).
const formatMeasurementDateSqlNaiveLocal = () => {
    const d = new Date();
    const pad = (n, len = 2) => String(n).padStart(len, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

/** Parses the same naive "yyyy-MM-dd hh:mm:ss.fff" shape for bin tile display. */
const parseMeasurementDateSqlNaiveForDisplay = (raw) => {
    if (!raw || typeof raw !== "string") return null;
    const s = raw.trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,7}))?/.exec(s);
    if (!m) {
        const t = Date.parse(s);
        return Number.isNaN(t) ? null : new Date(t);
    }
    let ms = 0;
    if (m[7]) {
        const frac = `${m[7]}000`.slice(0, 3);
        ms = parseInt(frac, 10);
        if (Number.isNaN(ms)) ms = 0;
    }
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6]), ms);
};

// Bin shape "Stice" is defined only by Temputech `CustomerSiteLayoutBin` (`layoutBins` on this payload).
// Editable dbo.BinDimension rows apply only to bins that do not appear on that layout map.

const shapeKindLabel = (sk) =>
    ({
        Round: "Round",
        Stice: "Stice",
        FlatHouse: "Flat House",
    }[sk] || sk || "—");

const inferShapeKindFromBinTypeOnly = (bin) => {
    const t = readString(bin, "Type", "type").toLowerCase();
    if (t.includes("flat") || t.includes("house")) return "FlatHouse";
    return "Round";
};

const normalizeShapeKindStored = (raw) => {
    if (!raw) return "";
    const sk = String(raw).trim();
    if (sk === "Slice") return "Round"; // legacy
    return sk;
};

const layoutBinIsRound = (layoutBin) =>
    layoutBin != null && (layoutBin.IsRound === true || layoutBin.isRound === true);

/** Reads numeric fields from the dimension draft (stored as number or string). */
const dimNum = (d, ...camelKeys) => {
    if (!d) return null;
    for (const k of camelKeys) {
        const raw = d[k];
        if (raw === "" || raw == null) continue;
        const n = typeof raw === "number" ? raw : parseFloat(raw);
        if (!Number.isNaN(n)) return n;
    }
    return null;
};

const seedDimDraftFromBinAndRow = (bin, savedRow, layoutBin) => {
    const nStr = (v) => (v != null && Number.isFinite(v) ? String(v) : "");
    if (layoutBin) {
        const L = readNumber(layoutBin, "Length", "length") ?? 0;
        const W = readNumber(layoutBin, "Width", "width") ?? 0;
        const H = readNumber(layoutBin, "Height", "height") ?? 0;
        const isRound = layoutBinIsRound(layoutBin);
        const D = Math.max(L, W);
        return {
            shapeKind: isRound ? "Round" : "Stice",
            layoutBacked: true,
            length: nStr(L),
            width: nStr(W),
            height: nStr(H),
            diameter: isRound ? nStr(D) : "",
            peakHeight: "",
            peakLength: "",
            dirty: false,
        };
    }

    let skSaved = savedRow ? normalizeShapeKindStored(readString(savedRow, "ShapeKind", "shapeKind")) : "";
    if (skSaved === "Stice") skSaved = "";
    const inferred = inferShapeKindFromBinTypeOnly(bin);
    const sk = skSaved || inferred;

    const fromSavedOrBin = (srLow, srUp, binLow, binUp) => {
        if (savedRow) {
            const a = readNumber(savedRow, srUp, srLow);
            if (a != null) return nStr(a);
        }
        const b = readNumber(bin, binUp, binLow);
        return nStr(b ?? null);
    };

    return {
        shapeKind: sk,
        layoutBacked: false,
        diameter: fromSavedOrBin("diameter", "Diameter", "diameter", "Diameter"),
        height: fromSavedOrBin("height", "Height", "height", "Height"),
        peakHeight: fromSavedOrBin("peakHeight", "PeakHeight", "peakHeight", "PeakHeight"),
        length: fromSavedOrBin("length", "Length", "length", "Length"),
        width: fromSavedOrBin("width", "Width", "width", "Width"),
        peakLength: fromSavedOrBin("peakLength", "PeakLength", "peakLength", "PeakLength"),
        dirty: false,
    };
};

const parseDimPayloadNumber = (v) => {
    if (v === "" || v == null) return null;
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isNaN(n) ? null : n;
};

// Capacity (cu ft). Priority: CustomerSiteLayoutBin → user dbo.BinDimension draft → legacy Bin row.
const computeBinTotalVolume = (bin, layoutBin, dimDraft) => {
    if (layoutBin) {
        const L = readNumber(layoutBin, "Length", "length") ?? 0;
        const W = readNumber(layoutBin, "Width", "width") ?? 0;
        const H = readNumber(layoutBin, "Height", "height") ?? 0;
        if (layoutBinIsRound(layoutBin)) {
            const D = Math.max(L, W);
            const r = D / 2;
            return Math.max(0, Math.PI * r * r * H);
        }
        return Math.max(0, L * W * H);
    }
    if (dimDraft && dimDraft.shapeKind && !dimDraft.layoutBacked) {
        const sk = dimDraft.shapeKind;
        if (sk === "FlatHouse") {
            const L = dimNum(dimDraft, "length") ?? readNumber(bin, "Length", "length") ?? 0;
            const W = dimNum(dimDraft, "width") ?? readNumber(bin, "Width", "width") ?? 0;
            const H = dimNum(dimDraft, "height") ?? readNumber(bin, "Height", "height") ?? 0;
            return Math.max(0, L * W * H);
        }
        if (sk === "Round") {
            const D = dimNum(dimDraft, "diameter") ?? readNumber(bin, "Diameter", "diameter") ?? 0;
            const H = dimNum(dimDraft, "height") ?? readNumber(bin, "Height", "height") ?? 0;
            const r = D / 2;
            return Math.max(0, Math.PI * r * r * H);
        }
    }
    if (!bin) return 0;
    const totalVolume = readNumber(bin, "TotalVolume", "totalVolume");
    if (totalVolume && totalVolume > 0) return totalVolume;
    const diameter = readNumber(bin, "Diameter", "diameter") ?? 0;
    const height = readNumber(bin, "Height", "height") ?? 0;
    const length = readNumber(bin, "Length", "length") ?? 0;
    const width = readNumber(bin, "Width", "width") ?? 0;
    const type = readString(bin, "Type", "type").toLowerCase();
    if (type.includes("flat") || type.includes("box") || type.includes("rect")) {
        return Math.max(0, length * width * height);
    }
    const r = diameter / 2;
    return Math.max(0, Math.PI * r * r * height);
};

const effectiveWallHeightFeet = (bin, layoutBin, dimDraft) => {
    if (layoutBin) return readNumber(layoutBin, "Height", "height") ?? 0;
    if (dimDraft && dimDraft.shapeKind && !dimDraft.layoutBacked) {
        const h = dimNum(dimDraft, "height");
        if (h != null && h > 0) return h;
    }
    return readNumber(bin, "Height", "height") ?? 0;
};

// Resolves bushel weight (lb/bushel) for a grain type. Resolution order:
//   1. BTS Grain reference table (per-site, user-tunable),
//   2. legacy Bin.BushelWeight (BTS desktop's last saved value),
//   3. USDA standard fallback table (so Product Weight always renders even on fresh sites
//      where the Grain table hasn't been seeded), then a generic 56 lb/bu safety default.
// Returns 0 only when grain is empty/unset, which intentionally zeros downstream weights.
const resolveBushelWeight = (grainName, grains, fallback) => {
    const norm = (s) => String(s ?? "").trim().toLowerCase();
    const target = norm(grainName);
    if (!target || target === "empty") return 0;

    if (grains && Array.isArray(grains)) {
        const match = grains.find((g) => norm(g.GrainType ?? g.grainType) === target);
        if (match) {
            const w = readNumber(match, "BushelWeight", "bushelWeight");
            if (w && w > 0) return w;
        }
    }
    if (fallback && fallback > 0) return fallback;

    if (DEFAULT_BUSHEL_WEIGHTS_LB[target] != null) return DEFAULT_BUSHEL_WEIGHTS_LB[target];
    // Soft-match: user-entered varieties like "yellow corn" or "hrw wheat" still find a sensible
    // weight by checking whether any default key is contained in the grain name.
    for (const key of Object.keys(DEFAULT_BUSHEL_WEIGHTS_LB)) {
        if (target.includes(key)) return DEFAULT_BUSHEL_WEIGHTS_LB[key];
    }
    return DEFAULT_FALLBACK_BUSHEL_WEIGHT_LB;
};

// Single source of truth for the modal's level math. Given the user's chosen fill mode + value
// plus the bin geometry / grain density, returns every BinLevel column the syncBinLevel payload
// needs. When `currentContents` is "Empty" we zero everything so the resulting BinLevel row is
// unambiguously an empty bin (some downstream consumers key off PercentFull == 0).
const computeBinLevel = ({
    bin, layoutBin, dimDraft, fillMode, fillValue, grainName, bushelWeight, bushelsPerCubicFoot,
}) => {
    const height = effectiveWallHeightFeet(bin, layoutBin, dimDraft);
    const totalVolume = computeBinTotalVolume(bin, layoutBin, dimDraft);
    const bpc = bushelsPerCubicFoot && bushelsPerCubicFoot > 0
        ? bushelsPerCubicFoot
        : (readNumber(bin, "BushelsPerCubicFoot", "bushelsPerCubicFoot") ?? DEFAULT_BUSHELS_PER_CUBIC_FOOT);

    const isEmpty = String(grainName ?? "").trim().toLowerCase() === "empty";
    const lbPerCuft = (bushelWeight && bushelWeight > 0) ? bushelWeight * bpc : 0;

    let percentFull = 0;
    if (!isEmpty) {
        const v = parseFloat(fillValue);
        if (!Number.isNaN(v) && v >= 0) {
            switch (fillMode) {
                case "percent":
                    percentFull = Math.max(0, Math.min(100, v));
                    break;
                case "height":
                    percentFull = height > 0 ? Math.max(0, Math.min(100, (v / height) * 100)) : 0;
                    break;
                case "headroom":
                    percentFull = height > 0 ? Math.max(0, Math.min(100, ((height - v) / height) * 100)) : 0;
                    break;
                case "bushels": {
                    if (totalVolume > 0 && bpc > 0) {
                        const totalBushels = totalVolume * bpc;
                        percentFull = totalBushels > 0 ? Math.max(0, Math.min(100, (v / totalBushels) * 100)) : 0;
                    }
                    break;
                }
                default:
                    percentFull = 0;
            }
        }
    }

    const productHeight = height * (percentFull / 100);
    const productVolume = totalVolume * (percentFull / 100);
    const productWeight = productVolume * lbPerCuft;
    const headroomHeight = Math.max(0, height - productHeight);
    const headroomVolume = Math.max(0, totalVolume - productVolume);
    const headroomWeight = headroomVolume * lbPerCuft;
    const measurementInFeet = headroomHeight; // top-down laser equivalent
    const measurementInMeters = measurementInFeet * FT_TO_M;
    const bushels = productVolume * bpc;

    return {
        percentFull: Math.round(percentFull),
        productHeight: round4(productHeight),
        productVolume: round2(productVolume),
        productWeight: round2(productWeight),
        headroomHeight: round4(headroomHeight),
        headroomVolume: round2(headroomVolume),
        headroomWeight: round2(headroomWeight),
        measurementInFeet: round4(measurementInFeet),
        measurementInMeters: round4(measurementInMeters),
        bushels: round2(bushels),
        totalBushels: round2(totalVolume * bpc),
    };
};

const round2 = (n) => (Number.isFinite(n) ? Math.round(n * 100) / 100 : 0);
const round4 = (n) => (Number.isFinite(n) ? Math.round(n * 10000) / 10000 : 0);

const toFixed = (n, d = 2) => (Number.isFinite(n) ? Number(n).toFixed(d) : "-");
const formatInt = (n) => (Number.isFinite(n) ? Math.round(n).toLocaleString() : "-");

// Picks the best initial fill mode + value for a bin based on what we already know:
// 1. existing BinLevel snapshot (gateway or prior user save) if present,
// 2. otherwise the legacy Bin row (BTS desktop's last saved state).
// Defaulting to PercentFull keeps the UI simple even when the existing row only has a height.
const deriveInitialGrain = (bin, existingLevel) => {
    // Match inventory summary exactly:
    // - BinLevel CurrentContents when present
    // - blank / missing BinLevel contents => Empty
    // Never fall back to BTS Bin.GrainType — that is often stale (e.g. Corn) while the
    // summary correctly shows Empty / 0% product from BinLevel (or no level row).
    if (existingLevel) {
        const fromLevel = readString(existingLevel, "CurrentContents", "currentContents");
        if (fromLevel) return fromLevel;
    }
    return "Empty";
};

const clampPercentFillValue = (pct) => {
    if (pct == null || !Number.isFinite(pct)) return "0";
    return String(Math.max(0, Math.min(100, Math.round(pct))));
};

// Always seeds Fill Mode as Percent Full so the editor matches the summary's product %.
const deriveInitialFill = (bin, existingLevel) => {
    const grain = deriveInitialGrain(bin, existingLevel);
    if (String(grain).trim().toLowerCase() === "empty") {
        return { fillMode: "percent", fillValue: "0" };
    }
    if (existingLevel) {
        const pct = readNumber(existingLevel, "PercentFull", "percentFull");
        if (pct != null) {
            return { fillMode: "percent", fillValue: clampPercentFillValue(pct) };
        }
        const ph = readNumber(existingLevel, "ProductHeight", "productHeight");
        const wall = readNumber(existingLevel, "BinHeight", "binHeight")
            ?? readNumber(bin, "Height", "height");
        if (ph != null && wall != null && wall > 0) {
            return { fillMode: "percent", fillValue: clampPercentFillValue((ph / wall) * 100) };
        }
    }
    // No usable BinLevel fill — keep Empty-aligned defaults (do not use BTS Bin.PercentFull).
    return { fillMode: "percent", fillValue: "0" };
};

const SiteInventoryModal = ({ isModalOpen, setIsModalOpen, singleBinContext = null }) => {
    const [sites, setSites] = useState([]);
    const [pickerSiteGuid, setPickerSiteGuid] = useState("");
    const [selectedSite, setSelectedSite] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState(null);
    const [inventory, setInventory] = useState({
        bins: [],
        grains: [],
        binLevels: [],
        binDimensions: [],
        lastBinLevelHistoryByBin: [],
        layoutBins: [],
    });
    // editById keyed by BinId so the form survives bin reorderings; { grain, fillMode, fillValue, dirty }
    const [editById, setEditById] = useState({});
    // Per-bin dimension drafts for dbo.BinDimension (BinLevel math only); seeded on load from Bin + saved row.
    const [dimByBinId, setDimByBinId] = useState({});
    const [dimPanelsOpen, setDimPanelsOpen] = useState(() => new Set());
    const [expandedBin, setExpandedBin] = useState(null);
    const [currentTheme, setCurrentTheme] = useState(global.gw.theme || "dark");
    const [forceUpdate, setForceUpdate] = useState(0);
    const binListScrollRef = useRef(null);

    /** True only when modal should focus one bin row (explicit binId from a site-card bin tile). */
    const hasSingleBinFocus = useMemo(() => {
        if (!singleBinContext) return false;
        const raw = singleBinContext.binId;
        if (raw == null || raw === "") return false;
        const n = typeof raw === "number" ? raw : Number.parseInt(String(raw), 10);
        return !Number.isNaN(n);
    }, [singleBinContext]);

    // Windows/Chrome often fails scroll chaining from nested MUI boxes onto the list's overflow
    // container, so wheel over the gutter works but wheel over a bin tile does not. Forward wheel
    // to the scrollable list explicitly (same pattern as SiteGradesModal scroll regions).
    const forwardWheelToBinList = useCallback((e) => {
        const scroller = binListScrollRef.current;
        if (!scroller) return;
        scroller.scrollTop += e.deltaY;
        e.preventDefault();
    }, []);

    useEffect(() => {
        const handle = () => {
            setCurrentTheme(global.gw.theme || "dark");
            setForceUpdate((p) => p + 1);
        };
        window.addEventListener("themeChanged", handle);
        return () => window.removeEventListener("themeChanged", handle);
    }, []);

    // Restrict the picker to non-HMS BTS sites with a real GUID — HMS sites have no Bin/BinLevel
    // tables so the inventory query would just return empty.
    // useLayoutEffect: apply carried siteGuid before paint so tool-switching never flashes the picker.
    useLayoutEffect(() => {
        if (!isModalOpen[0]) {
            setPickerSiteGuid("");
            setSelectedSite("");
            return;
        }
        const all = [...(global.gw.siteInfos || [])].filter((s) => s.siteGuid && !s.isHms);
        all.sort((a, b) => (a.siteName || "").toLowerCase().localeCompare((b.siteName || "").toLowerCase()));
        setSites(all);
        // Site-card / single-bin / tool-nav entry already knows the site — skip the picker.
        // Main-menu entry starts on SiteSelectPhase (no auto-select of the first site).
        if (singleBinContext?.siteGuid && all.some((s) => s.siteGuid === singleBinContext.siteGuid)) {
            setSelectedSite(singleBinContext.siteGuid);
            setPickerSiteGuid(singleBinContext.siteGuid);
            setLoading(true);
        } else {
            setSelectedSite("");
            setPickerSiteGuid("");
        }
        setSubmitMessage(null);
        setExpandedBin(null);
    }, [isModalOpen, singleBinContext]);

    useEffect(() => {
        if (!isModalOpen[0] || !hasSingleBinFocus || loading) return;
        const n = Number(singleBinContext.binId);
        if (Number.isNaN(n)) return;
        setExpandedBin(n);
    }, [isModalOpen, singleBinContext, loading, hasSingleBinFocus]);

    // Collapse expanders when switching sites from the header selector.
    useEffect(() => {
        if (hasSingleBinFocus) return;
        setExpandedBin(null);
    }, [selectedSite, hasSingleBinFocus]);

    const loadInventory = useCallback(async (siteGuid) => {
        if (!siteGuid) return;
        setLoading(true);
        setSubmitMessage(null);
        try {
            const res = await createApiEndpoint().fetchSiteInventory("direct", siteGuid);
            const data = await parseDecompressed(res, "getSiteInventory");
            if (data && typeof data === "object") {
                const bins = Array.isArray(data.bins) ? data.bins : [];
                const savedDims = Array.isArray(data.binDimensions) ? data.binDimensions : [];
                const layoutBinsRaw = Array.isArray(data.layoutBins) ? data.layoutBins : [];
                const savedById = new Map();
                for (const row of savedDims) {
                    const bid = readBinId(row);
                    if (bid != null) savedById.set(bid, row);
                }
                const layoutMap = new Map();
                for (const lb of layoutBinsRaw) {
                    const bid = readBinId(lb);
                    if (bid != null) layoutMap.set(bid, lb);
                }
                const nextDim = {};
                for (const bin of bins) {
                    const bid = readBinId(bin);
                    if (bid == null) continue;
                    nextDim[bid] = seedDimDraftFromBinAndRow(bin, savedById.get(bid), layoutMap.get(bid));
                }
                setInventory({
                    bins,
                    grains: Array.isArray(data.grains) ? data.grains : [],
                    binLevels: Array.isArray(data.binLevels) ? data.binLevels : [],
                    binDimensions: savedDims,
                    layoutBins: layoutBinsRaw,
                    lastBinLevelHistoryByBin: Array.isArray(data.lastBinLevelHistoryByBin) ? data.lastBinLevelHistoryByBin : [],
                });
                setDimByBinId(nextDim);
            } else {
                setInventory({
                    bins: [],
                    grains: [],
                    binLevels: [],
                    binDimensions: [],
                    layoutBins: [],
                    lastBinLevelHistoryByBin: [],
                });
                setDimByBinId({});
            }
            setEditById({});
            setExpandedBin(null);
        } catch (e) {
            console.warn("SiteInventoryModal: failed to load inventory", e);
            setInventory({
                bins: [],
                grains: [],
                binLevels: [],
                binDimensions: [],
                layoutBins: [],
                lastBinLevelHistoryByBin: [],
            });
        } finally {
            // Without this the modal stays stuck on the "Loading…" indicator on the success path
            // (only the catch branch was clearing it before), which looks like Site Inventory hangs.
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isModalOpen[0] && selectedSite) {
            loadInventory(selectedSite);
        }
    }, [isModalOpen, selectedSite, loadInventory]);

    const grainOptions = useMemo(() => {
        const fromGrains = (inventory.grains || [])
            .map((g) => readString(g, "GrainType", "grainType"))
            .filter((s) => s && s.toLowerCase() !== "empty");
        const unique = Array.from(new Set(["Empty", ...fromGrains]));
        return unique;
    }, [inventory.grains]);

    const displayBins = useMemo(() => {
        const raw = [...(inventory.bins || [])];
        if (!hasSingleBinFocus) return [];
        const target = Number(singleBinContext.binId);
        if (Number.isNaN(target)) return [];
        return raw.filter((bin) => readBinId(bin) === target);
    }, [inventory.bins, singleBinContext, hasSingleBinFocus]);

    const focusedBinName =
        displayBins[0] ? readString(displayBins[0], "Name", "name") || "" : "";

    // Same summary shape as InventoryWindow / site-card inventory (stats + by-grain + by-bin).
    const inventorySummary = useMemo(() => {
        const bins = inventory.bins || [];
        if (!bins.length) return null;
        const summaryBins = bins.map((b) => {
            const id = readBinId(b);
            return {
                binDataId: id,
                binId: id,
                number: readNumber(b, "Number", "number"),
                name: readString(b, "Name", "name"),
            };
        });
        return global.summarizeSiteInventoryTotalsFromBinLevelRows(
            inventory.binLevels || [],
            summaryBins,
            inventory.binDimensions || [],
            bins,
        );
    }, [inventory.bins, inventory.binLevels, inventory.binDimensions]);

    const levelByBinId = useMemo(() => {
        const map = new Map();
        for (const lvl of inventory.binLevels || []) {
            const id = readBinId(lvl);
            if (id != null) map.set(id, lvl);
        }
        return map;
    }, [inventory.binLevels]);

    const layoutBinByBinId = useMemo(() => {
        const map = new Map();
        for (const lb of inventory.layoutBins || []) {
            const bid = readBinId(lb);
            if (bid != null) map.set(bid, lb);
        }
        return map;
    }, [inventory.layoutBins]);

    const savedDimensionByBinId = useMemo(() => {
        const map = new Map();
        for (const row of inventory.binDimensions || []) {
            const bid = readBinId(row);
            if (bid != null) map.set(bid, row);
        }
        return map;
    }, [inventory.binDimensions]);

    const findLevelForBin = useCallback((bin) => {
        if (!bin) return null;
        const id = readBinId(bin);
        if (id != null && levelByBinId.has(id)) return levelByBinId.get(id);
        // Same matcher the summary table uses (binDataId / binId / number).
        return global.findBinLevelRowForCardBin(inventory.binLevels || [], {
            binDataId: bin.BinId ?? bin.binId ?? bin.Id ?? bin.id ?? id,
            binId: id,
            number: readNumber(bin, "Number", "number"),
        });
    }, [levelByBinId, inventory.binLevels]);

    // Edit toggles an inline editor under that bin row. Always re-seed from BinLevel so the
    // form matches the summary row (Empty / 0%), not stale BTS Bin.GrainType / PercentFull.
    const openBinEditor = useCallback((binId) => {
        if (binId == null) return;
        const collapsing = expandedBin === binId;
        setExpandedBin(collapsing ? null : binId);
        if (collapsing) return;

        setEditById((edits) => {
            const bin = (inventory.bins || []).find((b) => readBinId(b) === binId);
            if (!bin) return edits;
            const existingLevel = findLevelForBin(bin);
            const initGrain = deriveInitialGrain(bin, existingLevel);
            const initFill = deriveInitialFill(bin, existingLevel);
            return {
                ...edits,
                [binId]: {
                    grain: initGrain,
                    fillMode: "percent",
                    fillValue: initFill.fillValue,
                    dirty: false,
                },
            };
        });
    }, [expandedBin, inventory.bins, findLevelForBin]);

    // After expand, center the bin line + editor in the inventory scroll viewport.
    useEffect(() => {
        if (expandedBin == null) return;
        const scroller = binListScrollRef.current;
        if (!scroller) return;

        let cancelled = false;
        let raf2 = 0;
        const centerExpandedBin = () => {
            if (cancelled) return;
            const row = scroller.querySelector(`[data-inventory-bin-row="${expandedBin}"]`);
            const editor = scroller.querySelector(`[data-inventory-bin-id="${expandedBin}"]`);
            if (!row && !editor) return;

            const scrollerRect = scroller.getBoundingClientRect();
            const topRect = (row || editor).getBoundingClientRect();
            const bottomRect = (editor || row).getBoundingClientRect();
            const blockMid = (topRect.top + bottomRect.bottom) / 2;
            const viewMid = scrollerRect.top + scrollerRect.height / 2;
            const nextTop = scroller.scrollTop + (blockMid - viewMid);
            const maxTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
            scroller.scrollTo({
                top: Math.max(0, Math.min(maxTop, nextTop)),
                behavior: "smooth",
            });
        };

        // Wait for the editor row to mount and lay out before measuring.
        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(centerExpandedBin);
        });

        return () => {
            cancelled = true;
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
        };
    }, [expandedBin]);

    const ensureEditState = useCallback((bin) => {
        const id = readBinId(bin);
        if (id == null) return null;
        if (editById[id]) return editById[id];
        const existingLevel = findLevelForBin(bin);
        const initFill = deriveInitialFill(bin, existingLevel);
        const initGrain = deriveInitialGrain(bin, existingLevel);
        const next = {
            grain: initGrain,
            fillMode: "percent",
            fillValue: initFill.fillValue,
            dirty: false,
        };
        setEditById((prev) => ({ ...prev, [id]: next }));
        return next;
    }, [editById, findLevelForBin]);

    const updateDim = (binId, patch) => {
        setDimByBinId((prev) => {
            const cur = prev[binId];
            if (cur?.layoutBacked) return prev;
            return {
                ...prev,
                [binId]: { ...(cur ?? {}), ...patch, dirty: true },
            };
        });
    };

    const toggleDimPanel = (binId) => {
        setDimPanelsOpen((prev) => {
            const n = new Set(prev);
            if (n.has(binId)) n.delete(binId);
            else n.add(binId);
            return n;
        });
    };

    const updateEdit = (binId, patch) => {
        setEditById((prev) => {
            const cur = prev[binId] ?? {};
            const next = { ...cur, ...patch, dirty: true };
            const grainChangedToEmpty =
                patch.grain != null
                && String(patch.grain).trim().toLowerCase() === "empty"
                && String(cur.grain ?? "").trim().toLowerCase() !== "empty";
            if (grainChangedToEmpty) {
                next.fillMode = "percent";
                next.fillValue = "0";
            }
            return {
                ...prev,
                [binId]: next,
            };
        });
    };

    const editDirtyCount = Object.values(editById).filter((e) => e?.dirty).length;
    const dimensionEditDirtyCount = Object.values(dimByBinId).filter((d) => d?.dirty && !d.layoutBacked).length;

    // Materializes the syncBinLevel JSON payload from current edits + initial state for any bin
    // the user didn't touch. Sending all bins (not just dirty) keeps server BinLevel a strict
    // mirror of the user's intent for the whole site, which matches gateway semantics.
    const buildPayload = () => {
        const binLevels = [];
        const measurementDate = formatMeasurementDateSqlNaiveLocal();
        for (const bin of inventory.bins || []) {
            const id = readBinId(bin);
            if (id == null) continue;
            const existing = findLevelForBin(bin);
            const edit = editById[id] ?? (() => {
                const initFill = deriveInitialFill(bin, existing);
                return {
                    grain: deriveInitialGrain(bin, existing),
                    fillMode: "percent",
                    fillValue: initFill.fillValue,
                    dirty: false,
                };
            })();
            const layoutBin = layoutBinByBinId.get(id) ?? null;
            const dimDraft = dimByBinId[id] ?? seedDimDraftFromBinAndRow(bin, savedDimensionByBinId.get(id), layoutBin);
            const bushelsPerCubicFoot = readNumber(bin, "BushelsPerCubicFoot", "bushelsPerCubicFoot") ?? DEFAULT_BUSHELS_PER_CUBIC_FOOT;
            const bushelWeight = resolveBushelWeight(
                edit.grain,
                inventory.grains,
                readNumber(bin, "BushelWeight", "bushelWeight") ?? 0
            );
            const calc = computeBinLevel({
                bin,
                layoutBin,
                dimDraft,
                fillMode: edit.fillMode,
                fillValue: edit.fillValue,
                grainName: edit.grain,
                bushelWeight,
                bushelsPerCubicFoot,
            });
            const vesselId = readNumber(existing, "VesselID", "vesselID", "vesselId") ?? id;
            const vesselName = readString(existing, "VesselName", "vesselName") || readString(bin, "Name", "name") || `Bin ${id}`;
            const recNum = readNumber(existing, "RecNum", "recNum");
            binLevels.push({
                binId: id,
                vesselID: vesselId,
                vesselName,
                currentContents: edit.grain,
                measurementDate,
                measurementInFeet: calc.measurementInFeet,
                measurementInMeters: calc.measurementInMeters,
                productHeight: calc.productHeight,
                productVolume: calc.productVolume,
                productWeight: calc.productWeight,
                headroomHeight: calc.headroomHeight,
                headroomVolume: calc.headroomVolume,
                headroomWeight: calc.headroomWeight,
                percentFull: calc.percentFull,
                deviceAddress: readNumber(existing, "DeviceAddress", "deviceAddress") ?? null,
                deviceTypeID: readNumber(existing, "DeviceTypeID", "deviceTypeID") ?? null,
                recNum: recNum ?? null,
            });
        }
        return { binLevels };
    };

    const handleSave = async () => {
        if (!selectedSite) return;
        if (editDirtyCount === 0 && dimensionEditDirtyCount === 0) {
            setSubmitMessage({ type: "info", text: "Nothing to save." });
            return;
        }
        setSubmitting(true);
        setSubmitMessage(null);
        try {
            if (dimensionEditDirtyCount > 0) {
                const binDimensions = Object.keys(dimByBinId).map((k) => {
                    const binId = parseInt(k, 10);
                    const d = dimByBinId[binId];
                    if (!d?.dirty || d.layoutBacked) return null;
                    return {
                        binId,
                        shapeKind: d.shapeKind,
                        diameter: parseDimPayloadNumber(d.diameter),
                        height: parseDimPayloadNumber(d.height),
                        peakHeight: parseDimPayloadNumber(d.peakHeight),
                        length: parseDimPayloadNumber(d.length),
                        width: parseDimPayloadNumber(d.width),
                        peakLength: parseDimPayloadNumber(d.peakLength),
                    };
                }).filter(Boolean);
                const resDim = await createApiEndpoint().fetchSyncSiteBinDimensions(
                    "direct",
                    { binDimensions },
                    selectedSite
                );
                const dimResult = resDim?.data?.syncSiteBinDimensions;
                if (dimResult !== "Saved.") {
                    setSubmitMessage({ type: "error", text: dimResult || "Server did not save bin dimensions." });
                    return;
                }
            }

            if (editDirtyCount > 0 || dimensionEditDirtyCount > 0) {
                const payload = buildPayload();
                const res = await createApiEndpoint().fetchSyncBinLevel("direct", payload, selectedSite);
                const result = res?.data?.syncBinLevel;
                if (result === "Saved.") {
                    const parts = [];
                    if (dimensionEditDirtyCount > 0) parts.push(`dimensions (${dimensionEditDirtyCount} bin${dimensionEditDirtyCount === 1 ? "" : "s"})`);
                    if (editDirtyCount > 0) parts.push(`inventory (${editDirtyCount} bin${editDirtyCount === 1 ? "" : "s"})`);
                    setSubmitMessage({ type: "success", text: `Saved ${parts.join(" and ")}.` });
                    await loadInventory(selectedSite);
                    if (selectedSite === global.gw.selectedSite?.siteGuid) {
                        try { await global.setSiteBinLevels(); } catch { /* nothing */ }
                    }
                    if (typeof window !== "undefined" && window.dispatchEvent) {
                        window.dispatchEvent(
                            new CustomEvent("grainwave-site-inventory-saved", { detail: { siteGuid: selectedSite } })
                        );
                    }
                } else {
                    setSubmitMessage({ type: "error", text: result || "Server did not save inventory." });
                }
            }
        } catch (e) {
            setSubmitMessage({ type: "error", text: e?.message || "Failed to save." });
        } finally {
            setSubmitting(false);
        }
    };

    const text = getThemeColor("#333333", "#f4cc8e");
    const subtle = getThemeColor("#666666", "#a8abb1");
    const bg = getThemeColor("#ffffff", "#2d2d2d");
    const border = getThemeColor("#e0e0e0", "#3d3d3d");
    const sectionBg = getThemeColor("#f8f8f8", "#252525");
    const fieldBg = getThemeColor("#ffffff", "#2d2d2d");
    // Expanded form canvas (behind labels + inputs). Bin expander header stays on fieldBg.
    const contentBg = getThemeColor("#e8e8e8", "#1e1e1e");

    const renderBinEditorContent = (bin) => {
        const id = readBinId(bin);
        if (id == null) return null;
        const edit = editById[id] ?? ensureEditState(bin);
        if (!edit) return null;
        const layoutBin = layoutBinByBinId.get(id) ?? null;
        const dim = dimByBinId[id] ?? seedDimDraftFromBinAndRow(bin, savedDimensionByBinId.get(id), layoutBin);
        const bushelsPerCubicFoot = readNumber(bin, "BushelsPerCubicFoot", "bushelsPerCubicFoot") ?? DEFAULT_BUSHELS_PER_CUBIC_FOOT;
        const bushelWeight = resolveBushelWeight(
            edit.grain,
            inventory.grains,
            readNumber(bin, "BushelWeight", "bushelWeight") ?? 0
        );
        const calc = computeBinLevel({
            bin,
            layoutBin,
            dimDraft: dim,
            fillMode: edit.fillMode,
            fillValue: edit.fillValue,
            grainName: edit.grain,
            bushelWeight,
            bushelsPerCubicFoot,
        });
        const textFieldSx = {
            "& .MuiInputLabel-root": { color: subtle },
            "& .MuiInputLabel-root.Mui-focused": { color: text },
            "& .MuiOutlinedInput-root": {
                background: fieldBg,
                color: text,
                "& fieldset": { borderColor: getThemeColor("#cccccc", "rgba(244,204,142,0.5)") },
            },
        };

        return (
            <MuiMaterials.Box>
                <MuiMaterials.Box sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "12px",
                    marginBottom: "12px",
                }}>
                    <MuiMaterials.FormControl size="small" variant="outlined" fullWidth>
                        <MuiMaterials.InputLabel sx={{ color: subtle, "&.Mui-focused": { color: text } }}>
                            Grain
                        </MuiMaterials.InputLabel>
                        <MuiMaterials.Select
                            value={edit.grain}
                            label="Grain"
                            onChange={(e) => updateEdit(id, { grain: e.target.value })}
                            sx={{
                                background: fieldBg,
                                color: text,
                                "& fieldset": { borderColor: getThemeColor("#cccccc", "rgba(244,204,142,0.5)") },
                                "& .MuiSvgIcon-root": { color: subtle },
                            }}
                            MenuProps={withScrollHintMenuProps({
                                PaperProps: {
                                    sx: {
                                        backgroundColor: bg,
                                        "& .MuiMenuItem-root": { color: text },
                                    },
                                },
                            })}
                        >
                            {grainOptions.map((g) => (
                                <MuiMaterials.MenuItem key={g} value={g}>{g}</MuiMaterials.MenuItem>
                            ))}
                        </MuiMaterials.Select>
                    </MuiMaterials.FormControl>
                    <MuiMaterials.FormControl size="small" variant="outlined" fullWidth>
                        <MuiMaterials.InputLabel sx={{ color: subtle, "&.Mui-focused": { color: text } }}>
                            Fill Mode
                        </MuiMaterials.InputLabel>
                        <MuiMaterials.Select
                            value={edit.fillMode}
                            label="Fill Mode"
                            onChange={(e) => updateEdit(id, { fillMode: e.target.value })}
                            sx={{
                                background: fieldBg,
                                color: text,
                                "& fieldset": { borderColor: getThemeColor("#cccccc", "rgba(244,204,142,0.5)") },
                                "& .MuiSvgIcon-root": { color: subtle },
                            }}
                            MenuProps={withScrollHintMenuProps({
                                PaperProps: {
                                    sx: {
                                        backgroundColor: bg,
                                        "& .MuiMenuItem-root": { color: text },
                                    },
                                },
                            })}
                        >
                            {FILL_MODES.map((m) => (
                                <MuiMaterials.MenuItem key={m.id} value={m.id}>{m.label}</MuiMaterials.MenuItem>
                            ))}
                        </MuiMaterials.Select>
                    </MuiMaterials.FormControl>
                    <MuiMaterials.TextField
                        size="small"
                        label={FILL_MODES.find((m) => m.id === edit.fillMode)?.label ?? "Value"}
                        type="number"
                        inputProps={{ step: "0.1", min: "0" }}
                        value={edit.fillValue}
                        onChange={(e) => updateEdit(id, { fillValue: e.target.value })}
                        sx={{
                            "& .MuiInputLabel-root": { color: subtle },
                            "& .MuiInputLabel-root.Mui-focused": { color: text },
                            "& .MuiOutlinedInput-root": {
                                background: fieldBg,
                                color: text,
                                "& fieldset": { borderColor: getThemeColor("#cccccc", "rgba(244,204,142,0.5)") },
                            },
                        }}
                    />
                </MuiMaterials.Box>
                <MuiMaterials.Box
                    sx={{
                        marginTop: "4px",
                        marginBottom: "8px",
                        border: `1px solid ${border}`,
                        borderRadius: "8px",
                        overflow: "hidden",
                        background: fieldBg,
                    }}
                >
                    <MuiMaterials.Box
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleDimPanel(id);
                        }}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            padding: "8px 10px",
                            cursor: "pointer",
                            "&:hover": {
                                background: getThemeColor("rgba(0,0,0,0.03)", "rgba(255,255,255,0.04)"),
                            },
                        }}
                    >
                        {dimPanelsOpen.has(id)
                            ? <MuiMaterials.ExpandMoreIcon sx={{ color: text }} />
                            : <MuiMaterials.ChevronRightIcon sx={{ color: text }} />}
                        <MuiMaterials.Typography sx={{ color: text, fontSize: "13px", fontWeight: 600, flex: 1 }}>
                            Dimensions (Inventory only, not 3D)
                            {(dim.dirty && !dim.layoutBacked) ? ` — unsaved` : ""}
                        </MuiMaterials.Typography>
                    </MuiMaterials.Box>
                    <MuiMaterials.Collapse in={dimPanelsOpen.has(id)} unmountOnExit>
                        <MuiMaterials.Box
                            sx={{
                                padding: "10px 12px",
                                borderTop: `1px solid ${border}`,
                                backgroundColor: contentBg,
                            }}
                        >
                            <MuiMaterials.Box sx={{ marginBottom: "12px" }}>
                                <MuiMaterials.Typography sx={{ color: subtle, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    Bin type
                                </MuiMaterials.Typography>
                                <MuiMaterials.Typography sx={{ color: text, fontSize: "15px", fontWeight: 500 }}>
                                    {shapeKindLabel(dim.shapeKind)}
                                </MuiMaterials.Typography>
                            </MuiMaterials.Box>
                            {dim.layoutBacked && (
                                <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px", marginBottom: "10px" }}>
                                    Dimensions follow the site layout.
                                </MuiMaterials.Typography>
                            )}
                            {dim.layoutBacked && layoutBinIsRound(layoutBin) && (
                                <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px" }}>
                                    <MuiMaterials.TextField size="small" label="Diameter (ft)" value={dim.diameter ?? ""} InputProps={{ readOnly: true }} sx={textFieldSx} />
                                    <MuiMaterials.TextField size="small" label="Wall height (ft)" value={dim.height ?? ""} InputProps={{ readOnly: true }} sx={textFieldSx} />
                                </MuiMaterials.Box>
                            )}
                            {dim.layoutBacked && layoutBin && !layoutBinIsRound(layoutBin) && (
                                <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px" }}>
                                    <MuiMaterials.TextField size="small" label="Length (ft)" value={dim.length ?? ""} InputProps={{ readOnly: true }} sx={textFieldSx} />
                                    <MuiMaterials.TextField size="small" label="Width (ft)" value={dim.width ?? ""} InputProps={{ readOnly: true }} sx={textFieldSx} />
                                    <MuiMaterials.TextField size="small" label="Height (ft)" value={dim.height ?? ""} InputProps={{ readOnly: true }} sx={textFieldSx} />
                                </MuiMaterials.Box>
                            )}
                            {!dim.layoutBacked && dim.shapeKind === "Round" && (
                                <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px" }}>
                                    <MuiMaterials.TextField size="small" label="Diameter (ft)" type="number" inputProps={{ step: "0.01", min: "0" }} value={dim.diameter} onChange={(e) => updateDim(id, { diameter: e.target.value })} sx={textFieldSx} />
                                    <MuiMaterials.TextField size="small" label="Wall height (ft)" type="number" inputProps={{ step: "0.01", min: "0" }} value={dim.height} onChange={(e) => updateDim(id, { height: e.target.value })} sx={textFieldSx} />
                                    <MuiMaterials.TextField size="small" label="Peak height (ft)" type="number" inputProps={{ step: "0.01", min: "0" }} value={dim.peakHeight} onChange={(e) => updateDim(id, { peakHeight: e.target.value })} sx={textFieldSx} />
                                </MuiMaterials.Box>
                            )}
                            {!dim.layoutBacked && dim.shapeKind === "FlatHouse" && (
                                <MuiMaterials.Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px" }}>
                                    <MuiMaterials.TextField size="small" label="Length (ft)" type="number" inputProps={{ step: "0.01", min: "0" }} value={dim.length} onChange={(e) => updateDim(id, { length: e.target.value })} sx={textFieldSx} />
                                    <MuiMaterials.TextField size="small" label="Width (ft)" type="number" inputProps={{ step: "0.01", min: "0" }} value={dim.width} onChange={(e) => updateDim(id, { width: e.target.value })} sx={textFieldSx} />
                                    <MuiMaterials.TextField size="small" label="Height (ft)" type="number" inputProps={{ step: "0.01", min: "0" }} value={dim.height} onChange={(e) => updateDim(id, { height: e.target.value })} sx={textFieldSx} />
                                    <MuiMaterials.TextField size="small" label="Peak length (ft)" type="number" inputProps={{ step: "0.01", min: "0" }} value={dim.peakLength} onChange={(e) => updateDim(id, { peakLength: e.target.value })} sx={textFieldSx} />
                                </MuiMaterials.Box>
                            )}
                        </MuiMaterials.Box>
                    </MuiMaterials.Collapse>
                </MuiMaterials.Box>
                <MuiMaterials.Box sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: "8px 16px",
                    padding: "8px 0",
                    borderTop: `1px dashed ${border}`,
                }}>
                    <ReadOnlyStat label="Percent Full" value={`${calc.percentFull}%`} subtle={subtle} text={text} />
                    <ReadOnlyStat label="Grain Height" value={`${toFixed(calc.productHeight, 2)} ft`} subtle={subtle} text={text} />
                    <ReadOnlyStat label="Headroom" value={`${toFixed(calc.headroomHeight, 2)} ft`} subtle={subtle} text={text} />
                    <ReadOnlyStat label="Measurement (laser)" value={`${toFixed(calc.measurementInFeet, 2)} ft`} subtle={subtle} text={text} />
                    <ReadOnlyStat label="Bushels" value={formatInt(calc.bushels)} subtle={subtle} text={text} />
                    <ReadOnlyStat label="Total Capacity" value={`${formatInt(calc.totalBushels)} bu`} subtle={subtle} text={text} />
                    <ReadOnlyStat label="Product Volume" value={`${formatInt(calc.productVolume)} cuft`} subtle={subtle} text={text} />
                    <ReadOnlyStat label="Product Weight" value={`${formatInt(calc.productWeight)} lb`} subtle={subtle} text={text} />
                </MuiMaterials.Box>
            </MuiMaterials.Box>
        );
    };

    const renderInlineBinEditor = (summaryBin) => {
        const id = summaryBin?.binId;
        if (id == null) return null;
        const bin = (inventory.bins || []).find((b) => readBinId(b) === id);
        if (!bin) {
            return (
                <MuiMaterials.Typography sx={{ color: subtle, fontSize: 13 }}>
                    This bin was not found in site inventory.
                </MuiMaterials.Typography>
            );
        }
        return renderBinEditorContent(bin);
    };

    const renderBinRow = (bin) => {
        const id = readBinId(bin);
        if (id == null) return null;
        const existing = findLevelForBin(bin);
        const edit = editById[id] ?? ensureEditState(bin);
        if (!edit) return null;
        const layoutBin = layoutBinByBinId.get(id) ?? null;
        const dim = dimByBinId[id] ?? seedDimDraftFromBinAndRow(bin, savedDimensionByBinId.get(id), layoutBin);
        const isExpanded = expandedBin === id;
        const isSingleBinMode = hasSingleBinFocus;
        const collapseOpen = isSingleBinMode || isExpanded;
        const bushelsPerCubicFoot = readNumber(bin, "BushelsPerCubicFoot", "bushelsPerCubicFoot") ?? DEFAULT_BUSHELS_PER_CUBIC_FOOT;
        const bushelWeight = resolveBushelWeight(
            edit.grain,
            inventory.grains,
            readNumber(bin, "BushelWeight", "bushelWeight") ?? 0
        );
        const calc = computeBinLevel({
            bin,
            layoutBin,
            dimDraft: dim,
            fillMode: edit.fillMode,
            fillValue: edit.fillValue,
            grainName: edit.grain,
            bushelWeight,
            bushelsPerCubicFoot,
        });
        const binName = readString(bin, "Name", "name") || `Bin ${id}`;
        const existingPct = readNumber(existing, "PercentFull", "percentFull");
        const existingGrain = readString(existing, "CurrentContents", "currentContents");
        const dateStr = (() => {
            const raw = readString(existing, "MeasurementDate", "measurementDate");
            if (!raw) return "";
            const d = parseMeasurementDateSqlNaiveForDisplay(raw);
            if (!d || isNaN(d.getTime())) return "";
            try {
                return d.toLocaleString("en-us", global.dateFormat || {}).replace(", ", " ");
            } catch {
                return "";
            }
        })();

        const textFieldSx = {
            "& .MuiInputLabel-root": { color: subtle },
            "& .MuiInputLabel-root.Mui-focused": { color: text },
            "& .MuiOutlinedInput-root": {
                background: fieldBg,
                color: text,
                "& fieldset": { borderColor: getThemeColor("#cccccc", "rgba(244,204,142,0.5)") },
            },
        };

        const isDirty = edit.dirty || (dim.dirty && !dim.layoutBacked);

        return (
            <MuiMaterials.Box
                key={`bin-${id}`}
                data-inventory-bin-id={id}
                onWheel={forwardWheelToBinList}
                sx={{
                    backgroundColor: fieldBg,
                    border: `1px solid ${border}`,
                    borderRadius: "8px",
                    mb: 1,
                    overflow: "hidden",
                    background: isDirty
                        ? getThemeColor("#fff8e6", "#3d361f")
                        : fieldBg,
                }}
            >
                <MuiMaterials.Box
                    onClick={isSingleBinMode ? undefined : () => setExpandedBin(isExpanded ? null : id)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        cursor: isSingleBinMode ? "default" : "pointer",
                        ...(isSingleBinMode
                            ? {}
                            : {
                                  "&:hover": {
                                      background: getThemeColor("rgba(0,0,0,0.03)", "rgba(255,255,255,0.03)"),
                                  },
                              }),
                    }}
                >
                    {!isSingleBinMode &&
                        (isExpanded ? <MuiMaterials.ExpandMoreIcon sx={{ color: text }} /> : <MuiMaterials.ChevronRightIcon sx={{ color: text }} />)}
                    <MuiMaterials.Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                        <MuiMaterials.Typography sx={{ color: text, fontSize: "14px", fontWeight: 600 }}>
                            {binName}
                        </MuiMaterials.Typography>
                        <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px" }}>
                            {existing
                                ? `${existingGrain || "—"} · ${existingPct != null ? `${Math.round(existingPct)}%` : "—"}${dateStr ? ` · ${dateStr}` : ""}`
                                : "No BinLevel snapshot — using BTS Bin defaults"}
                        </MuiMaterials.Typography>
                    </MuiMaterials.Box>
                    <MuiMaterials.Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: "120px" }}>
                        <MuiMaterials.Typography sx={{ color: text, fontSize: "14px", fontWeight: 600 }}>
                            {edit.grain || "—"}
                        </MuiMaterials.Typography>
                        <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px" }}>
                            {`${calc.percentFull}% · ${formatInt(calc.bushels)} bu`}
                        </MuiMaterials.Typography>
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
                <MuiMaterials.Collapse in={collapseOpen} unmountOnExit>
                    <MuiMaterials.Box
                        sx={{
                            padding: "12px 16px 16px",
                            borderTop: `1px solid ${border}`,
                            backgroundColor: contentBg,
                        }}
                    >
                        {renderBinEditorContent(bin)}
                    </MuiMaterials.Box>
                </MuiMaterials.Collapse>
            </MuiMaterials.Box>
        );
    };

    const dirtyTotal = editDirtyCount + dimensionEditDirtyCount;
    const handleClose = () => setIsModalOpen([false]);
    // Prefer state, but honor the carried siteGuid on the opening frame so tool-switching
    // does not flash SiteSelectPhase before the open effect runs.
    const activeSiteGuid =
        selectedSite
        || (isModalOpen[0] ? singleBinContext?.siteGuid : "")
        || "";
    const phase = activeSiteGuid || hasSingleBinFocus ? "edit" : "select";

    return (
        <ModalChrome
            open={isModalOpen[0]}
            onClose={handleClose}
            title={hasSingleBinFocus
                ? (focusedBinName ? `Inventory — ${focusedBinName}` : "Bin inventory")
                : "Site Inventory"}
            toolTitle="Inventory"
            siteWindowId={SITE_WINDOW_IDS.inventory}
            siteGuid={activeSiteGuid || singleBinContext?.siteGuid || null}
            showHeaderSiteSelect={phase === "edit" && !hasSingleBinFocus}
            onHeaderSiteChange={(guid) => {
                setPickerSiteGuid(guid);
                setSelectedSite(guid);
            }}
            headerSites={sites}
            actions={
                phase === "edit" ? (
                    <MuiMaterials.Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                        <MuiMaterials.Button
                            size="small"
                            variant="contained"
                            onClick={handleSave}
                            disabled={submitting || loading || dirtyTotal === 0}
                            sx={containedActionButtonSx()}
                        >
                            {submitting ? "Saving..." : dirtyTotal > 0 ? `Save (${dirtyTotal})` : "Save"}
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                ) : null
            }
            className="siteFilterModal hide-scrollbar"
            loadingOverlay={phase === "edit" && loading ? <CenteredLoadingOverlay /> : null}
        >
            {phase === "select" ? (
                <SiteSelectPhase
                    value={pickerSiteGuid}
                    onChange={setPickerSiteGuid}
                    onContinue={(guid) => {
                        setPickerSiteGuid(guid);
                        setSelectedSite(guid);
                    }}
                    onCancel={handleClose}
                    filterSite={(s) => !s.isHms}
                />
            ) : (
            <ModalBody className="siteFilterModalContent hide-scrollbar">
                {submitMessage ? (
                    <MuiMaterials.Typography
                        sx={{
                            marginBottom: "8px",
                            fontSize: "14px",
                            flexShrink: 0,
                            color: submitMessage.type === "success"
                                ? "#4caf50"
                                : submitMessage.type === "info"
                                    ? subtle
                                    : "#f44336",
                        }}
                    >
                        {submitMessage.text}
                    </MuiMaterials.Typography>
                ) : null}
                <MuiMaterials.Box
                    ref={binListScrollRef}
                    className="hide-scrollbar"
                    sx={{
                        width: "100%",
                        flex: "1 1 auto",
                        minHeight: 0,
                        overflowY: "auto",
                        overflowX: "hidden",
                        overscrollBehavior: "contain",
                        touchAction: "pan-y",
                        WebkitOverflowScrolling: "touch",
                        px: { xs: 0.5, sm: 1 },
                        pb: 2,
                    }}
                >
                    {loading ? null : (inventory.bins || []).length === 0 ? (
                        <MuiMaterials.Typography sx={{ color: subtle, padding: "12px", textAlign: "center" }}>
                            {selectedSite
                                ? "No BTS bins found for this site."
                                : "Select a site to view inventory."}
                        </MuiMaterials.Typography>
                    ) : hasSingleBinFocus && displayBins.length === 0 ? (
                        <MuiMaterials.Typography sx={{ color: subtle, padding: "12px", textAlign: "center" }}>
                            This bin was not found in site inventory.
                        </MuiMaterials.Typography>
                    ) : (
                        <MuiMaterials.Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: { xs: 2, sm: 2.5 },
                                width: "100%",
                                maxWidth: '100%',
                                mx: "auto",
                            }}
                        >
                            {!hasSingleBinFocus && inventorySummary ? (
                                <InventorySummaryTable
                                    summary={inventorySummary}
                                    siteGuid={selectedSite}
                                    expandedBinId={expandedBin}
                                    onEditBin={openBinEditor}
                                    renderBinEditor={renderInlineBinEditor}
                                />
                            ) : null}
                            {hasSingleBinFocus && displayBins.length > 0
                                ? displayBins.map(renderBinRow)
                                : null}
                        </MuiMaterials.Box>
                    )}
                </MuiMaterials.Box>
            </ModalBody>
            )}
        </ModalChrome>
    );
};

const ReadOnlyStat = ({ label, value, subtle, text }) => (
    <MuiMaterials.Box>
        <MuiMaterials.Typography sx={{ color: subtle, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {label}
        </MuiMaterials.Typography>
        <MuiMaterials.Typography sx={{ color: text, fontSize: "14px", fontWeight: 500 }}>
            {value}
        </MuiMaterials.Typography>
    </MuiMaterials.Box>
);

export default SiteInventoryModal;
